/**
 * Content model for the admin CMS.
 *
 * Every editable piece of the website lives in `public.cms_entries` as a row:
 *   kind   — what type of content it is ("post", "service", "block", ...)
 *   key    — stable identifier / slug, unique per kind
 *   data   — the live (published) content
 *   draft  — pending edits, not yet visible on the site
 *   status — "draft" (invisible to the public) or "published"
 */

export type CmsKind =
  | "block"
  | "post"
  | "service"
  | "plan"
  | "testimonial"
  | "faq"
  | "team"
  | "industry"
  | "differentiator"
  | "process"
  | "stat";

export type CmsValue = string | number | boolean | null | string[];
export type CmsData = Record<string, CmsValue>;

export type CmsEntry = {
  id: string;
  kind: CmsKind;
  key: string;
  data: CmsData;
  draft: CmsData | null;
  status: "draft" | "published";
  sort_order: number;
  published_at: string | null;
  updated_at: string;
};

export type FieldType = "text" | "textarea" | "richtext" | "list" | "number" | "boolean" | "image";

export type FieldDef = {
  name: string;
  label: string;
  type: FieldType;
  help?: string;
};

export type KindDef = {
  kind: CmsKind;
  label: string;
  plural: string;
  /** Field used as the row title in the admin list. */
  titleField: string;
  description: string;
  fields: FieldDef[];
};

export const CMS_KINDS: KindDef[] = [
  {
    kind: "block",
    label: "Page text block",
    plural: "Page content",
    titleField: "label",
    description: "Headlines, intro copy and calls to action used across the site.",
    fields: [
      { name: "label", label: "Where it appears", type: "text" },
      { name: "value", label: "Text", type: "textarea" },
    ],
  },
  {
    kind: "post",
    label: "Blog post",
    plural: "Blog posts",
    titleField: "title",
    description: "Articles and guides published under /blog.",
    fields: [
      { name: "title", label: "Title", type: "text" },
      { name: "category", label: "Category", type: "text" },
      {
        name: "excerpt",
        label: "Excerpt",
        type: "textarea",
        help: "Shown in listings and link previews.",
      },
      { name: "body", label: "Body", type: "richtext" },
      { name: "featuredImage", label: "Featured image URL", type: "image" },
      { name: "readingTime", label: "Reading time", type: "text" },
      { name: "publishedAt", label: "Publish date", type: "text", help: "YYYY-MM-DD" },
    ],
  },
  {
    kind: "service",
    label: "Service",
    plural: "Services",
    titleField: "title",
    description: "Service cards shown on the home and services pages.",
    fields: [
      { name: "title", label: "Title", type: "text" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "points", label: "Highlights", type: "list" },
    ],
  },
  {
    kind: "plan",
    label: "Pricing plan",
    plural: "Pricing plans",
    titleField: "name",
    description: "Pricing tiers, rates and feature lists.",
    fields: [
      { name: "name", label: "Plan name", type: "text" },
      { name: "badge", label: "Badge", type: "text" },
      { name: "monthly", label: "Monthly rate", type: "text" },
      { name: "originalMonthly", label: "Anchor (crossed-out) rate", type: "text" },
      { name: "hourly", label: "Hourly rate", type: "text" },
      {
        name: "customLabel",
        label: "Custom price label",
        type: "text",
        help: 'Use instead of rates, e.g. "Let\'s Talk!"',
      },
      { name: "featured", label: "Highlight this plan", type: "boolean" },
      { name: "features", label: "Features", type: "list" },
      { name: "includes", label: "Includes", type: "list" },
      { name: "cta", label: "Button label", type: "text" },
    ],
  },
  {
    kind: "testimonial",
    label: "Testimonial",
    plural: "Testimonials",
    titleField: "name",
    description: "Client quotes shown on the home page.",
    fields: [
      { name: "quote", label: "Quote", type: "textarea" },
      { name: "name", label: "Founder name", type: "text" },
      { name: "role", label: "Role", type: "text" },
      { name: "company", label: "Company", type: "text" },
      { name: "location", label: "Location", type: "text" },
      { name: "metric", label: "Outcome metric headline", type: "text" },
      { name: "highlight", label: "Outcome highlight detail", type: "text" },
      { name: "plan", label: "Finendra plan", type: "text" },
      { name: "stage", label: "Company stage / size", type: "text" },
      { name: "avatar", label: "Avatar image URL", type: "text" },
      { name: "stack", label: "Software stack", type: "list" },
    ],
  },
  {
    kind: "faq",
    label: "FAQ",
    plural: "FAQs",
    titleField: "q",
    description: "Questions and answers on the home and FAQ pages.",
    fields: [
      { name: "q", label: "Question", type: "text" },
      { name: "a", label: "Answer", type: "textarea" },
    ],
  },
  {
    kind: "team",
    label: "Team member",
    plural: "Team",
    titleField: "name",
    description: "People shown on the about page.",
    fields: [
      { name: "name", label: "Name", type: "text" },
      { name: "role", label: "Role", type: "text" },
      { name: "bio", label: "Short bio", type: "textarea" },
      { name: "photo", label: "Photo URL", type: "image" },
    ],
  },
  {
    kind: "industry",
    label: "Industry",
    plural: "Industries",
    titleField: "name",
    description: "Industries served, shown on the industries page.",
    fields: [
      { name: "name", label: "Industry", type: "text" },
      { name: "note", label: "Note", type: "textarea" },
    ],
  },
  {
    kind: "differentiator",
    label: "Differentiator",
    plural: "Why Finendra",
    titleField: "title",
    description: "Reasons to choose Finendra.",
    fields: [
      { name: "title", label: "Title", type: "text" },
      { name: "body", label: "Body", type: "textarea" },
    ],
  },
  {
    kind: "process",
    label: "Process step",
    plural: "Process steps",
    titleField: "title",
    description: "The how-it-works timeline.",
    fields: [
      { name: "step", label: "Step number", type: "text" },
      { name: "title", label: "Title", type: "text" },
      { name: "body", label: "Body", type: "textarea" },
    ],
  },
  {
    kind: "stat",
    label: "Stat",
    plural: "Stats",
    titleField: "label",
    description: "Headline numbers shown on the home page.",
    fields: [
      { name: "value", label: "Value", type: "number" },
      { name: "suffix", label: "Suffix", type: "text" },
      { name: "label", label: "Label", type: "text" },
    ],
  },
];

export function kindDef(kind: CmsKind): KindDef {
  return CMS_KINDS.find((k) => k.kind === kind) ?? CMS_KINDS[0]!;
}

export function entryTitle(entry: Pick<CmsEntry, "kind" | "key" | "data" | "draft">): string {
  const def = kindDef(entry.kind);
  const source = entry.draft ?? entry.data;
  const value = source[def.titleField];
  return typeof value === "string" && value.trim() ? value : entry.key;
}

/** Content an editor is currently working on: draft edits win over live data. */
export function editableData(entry: Pick<CmsEntry, "data" | "draft">): CmsData {
  return { ...entry.data, ...(entry.draft ?? {}) };
}

export function hasPendingDraft(entry: Pick<CmsEntry, "draft">): boolean {
  return entry.draft !== null && Object.keys(entry.draft).length > 0;
}

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

/**
 * Published entries of one kind as plain objects, ordered by sort order.
 * Falls back to the values shipped in code when nothing is published yet,
 * so the site never renders empty while content is being set up.
 */
export function publishedList<T>(entries: CmsEntry[], kind: CmsKind, fallback: T[]): T[] {
  const rows = entries
    .filter((e) => e.kind === kind && e.status === "published")
    .sort((a, b) => a.sort_order - b.sort_order || a.key.localeCompare(b.key));
  if (rows.length === 0) return fallback;
  return rows.map((r) => ({ ...(r.data as object), _key: r.key }) as T);
}

/** A single published text block, falling back to the copy shipped in code. */
export function publishedBlock(entries: CmsEntry[], key: string, fallback: string): string {
  const row = entries.find((e) => e.kind === "block" && e.key === key && e.status === "published");
  const value = row?.data["value"];
  return typeof value === "string" && value.trim() ? value : fallback;
}
